// Helper utility & hook for 24-hour order payment countdown timer
import { useState, useEffect } from 'react';

export const ORDER_TIMER_DURATION = 24 * 60 * 60 * 1000; // 24 hours in ms

/**
 * Initialize order timer start time in localStorage if not already set
 */
export const initOrderTimer = (orderId, txCode, timestamp = Date.now()) => {
    const timeStr = String(timestamp);
    if (orderId && !localStorage.getItem(`ORDER_TIMER_START_${orderId}`)) {
        localStorage.setItem(`ORDER_TIMER_START_${orderId}`, timeStr);
    }
    if (txCode && !localStorage.getItem(`ORDER_TIMER_START_${txCode}`)) {
        localStorage.setItem(`ORDER_TIMER_START_${txCode}`, timeStr);
    }
};

/**
 * Get remaining time for an order
 */
export const getOrderTimer = (orderId, txCode, txDate) => {
    let startTime = null;
    const startOrderId = orderId ? localStorage.getItem(`ORDER_TIMER_START_${orderId}`) : null;
    const startTxCode = txCode ? localStorage.getItem(`ORDER_TIMER_START_${txCode}`) : null;

    if (startOrderId && startTxCode) {
        startTime = Math.min(parseInt(startOrderId, 10), parseInt(startTxCode, 10));
    } else if (startOrderId) {
        startTime = parseInt(startOrderId, 10);
    } else if (startTxCode) {
        startTime = parseInt(startTxCode, 10);
    }

    if (!startTime || isNaN(startTime)) {
        if (txDate) {
            try {
                const parsedDate = new Date(txDate).getTime();
                const now = new Date();
                const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
                
                // If txDate is from today, start countdown from now
                if (String(txDate).startsWith(todayStr)) {
                    startTime = Date.now();
                    initOrderTimer(orderId, txCode, startTime);
                } else if (!isNaN(parsedDate)) {
                    // Older date, use transaction date timestamp (will be expired if > 24h)
                    startTime = parsedDate;
                } else {
                    startTime = Date.now();
                    initOrderTimer(orderId, txCode, startTime);
                }
            } catch (e) {
                startTime = Date.now();
                initOrderTimer(orderId, txCode, startTime);
            }
        } else {
            startTime = Date.now();
            initOrderTimer(orderId, txCode, startTime);
        }
    }

    const expiresAt = startTime + ORDER_TIMER_DURATION;
    const remaining = expiresAt - Date.now();

    if (remaining <= 0) {
        return {
            remaining: 0,
            hours: 0,
            minutes: 0,
            seconds: 0,
            formatted: '00:00:00',
            isExpired: true,
        };
    }

    const totalSecs = Math.floor(remaining / 1000);
    const hours = Math.floor(totalSecs / 3600);
    const minutes = Math.floor((totalSecs % 3600) / 60);
    const seconds = totalSecs % 60;

    const pad = (n) => String(n).padStart(2, '0');
    return {
        remaining,
        hours,
        minutes,
        seconds,
        formatted: `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`,
        isExpired: false,
    };
};

/**
 * React hook to track live countdown timer for an order
 */
export const useOrderTimer = (orderId, txCode, txDate) => {
    const [timer, setTimer] = useState(() => getOrderTimer(orderId, txCode, txDate));

    useEffect(() => {
        // Immediate check & update
        setTimer(getOrderTimer(orderId, txCode, txDate));

        const interval = setInterval(() => {
            const current = getOrderTimer(orderId, txCode, txDate);
            setTimer(current);
            if (current.isExpired) {
                clearInterval(interval);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [orderId, txCode, txDate]);

    return timer;
};
